/**
 * ONE place for every Shopify cart link on the site.
 *
 * Paste either the full cart link (https://shop.gritvuegear.com/cart/45095500546141:1)
 * or just the variant ID (45095500546141). The quantity at the end is set
 * automatically, so one link per product is enough.
 *
 * An empty value never sends anyone to a broken page or to a similar product:
 * the button shows a short "checkout link not connected yet" notice instead.
 *
 * PUBLIC products appear as cards. PRIVATE products only appear inside the
 * checkout negotiation at a specific decision point, never as public cards.
 * RETENTION products only appear after a customer declines an earlier offer.
 */
export const PRODUCT_LINKS = {
  // ---------- Single X1 ----------
  /** SINGLE_X1_VARIANT_ID · PUBLIC · 1 X1 · $49.95 · one-year coverage · shipping calculated at checkout · 5OFF allowed */
  singleX1: 'https://shop.gritvuegear.com/cart/45095500546141:1',
  /** SINGLE_X1_LIFETIME_NORMAL_6990_VARIANT_ID · PUBLIC option · 1 X1 + Lifetime · $69.90 · shipping calculated at checkout */
  singleX1LifetimeNormal6990: 'https://shop.gritvuegear.com/cart/48896462880861:1',
  /** SINGLE_X1_LIFETIME_PRIVATE_5990_VARIANT_ID · PRIVATE · 1 X1 + Lifetime · $59.90 · FREE SHIPPING */
  singleX1LifetimePrivate5990: 'https://shop.gritvuegear.com/cart/48896554041437:1',
  /** SINGLE_X1_LIFETIME_FINAL_5790_VARIANT_ID · PRIVATE · 1 X1 + Lifetime · $57.90 · FREE SHIPPING · final offer after declining $59.90 */
  singleX1LifetimeFinal5790: 'https://shop.gritvuegear.com/cart/48896572162141:1',
  /** SINGLE_X1_LIFETIME_FREE_SHIPPING_RESCUE_6990_VARIANT_ID · RETENTION · 1 X1 + Lifetime · $69.90 · FREE SHIPPING */
  singleX1LifetimeFreeShipping6990: 'https://shop.gritvuegear.com/cart/48896644284509:1',

  // ---------- 3 X1 and its private expansion ----------
  /** THREE_X1_PUBLIC_9990_VARIANT_ID · PUBLIC · Buy 2 Get 1 Free · 3 X1 · $99.90 ($33.30/X1) · lifetime on all 3 · FREE SHIPPING */
  threeX1Public9990: 'https://shop.gritvuegear.com/cart/45095660224605:1',
  /** FOUR_X1_PRIVATE_11990_VARIANT_ID · PRIVATE · 4 X1 · $119.90 ($29.98/X1) · lifetime on all 4 · FREE SHIPPING */
  fourX1Private11990: 'https://shop.gritvuegear.com/cart/48896690323549:1',
  /** FIVE_X1_PRIVATE_14490_VARIANT_ID · PRIVATE · 5 X1 · $144.90 ($28.98/X1) · lifetime on all 5 · FREE SHIPPING */
  fiveX1Private14490: 'https://shop.gritvuegear.com/cart/48896703103069:1',
  /** SIX_X1_PRIVATE_16990_VARIANT_ID · PRIVATE · 6 X1 · $169.90 ($28.32/X1) · lifetime on all 6 · FREE SHIPPING */
  sixX1Private16990: 'https://shop.gritvuegear.com/cart/48896709787741:1',

  // ---------- 6 X1 and its private expansion ----------
  /** SIX_X1_PUBLIC_17990_VARIANT_ID · PUBLIC · Buy 4 Get 2 Free · 6 X1 · $179.90 ($29.98/X1) · lifetime on all 6 · FREE SHIPPING */
  sixX1Public17990: 'https://shop.gritvuegear.com/cart/45095747879005:1',
  /** EIGHT_X1_PRIVATE_21990_VARIANT_ID · PRIVATE · 8 X1 · $219.90 ($27.49/X1) · lifetime on all 8 · FREE SHIPPING */
  eightX1Private21990: 'https://shop.gritvuegear.com/cart/48896716374109:1',
  /** TEN_X1_PRIVATE_25990_VARIANT_ID · PRIVATE · 10 X1 · $259.90 ($25.99/X1) · lifetime on all 10 · FREE SHIPPING */
  tenX1Private25990: 'https://shop.gritvuegear.com/cart/48896722239581:1',
  /** TWELVE_X1_PRIVATE_29990_VARIANT_ID · PRIVATE · 12 X1 · $299.90 ($24.99/X1) · lifetime on all 12 · FREE SHIPPING */
  twelveX1Private29990: 'https://shop.gritvuegear.com/cart/48896727679069:1',

  // ---------- Business ----------
  /** THIRTY_X1_PUBLIC_69990_VARIANT_ID · PUBLIC · Buy 20 Get 10 Free · 30 X1 · $699.90 ($23.33/X1) · lifetime on all 30 · FREE SHIPPING */
  thirtyX1Public69990: 'https://shop.gritvuegear.com/cart/45095754858589:1',
  /** FORTY_X1_BUSINESS_PRIVATE_89990_VARIANT_ID · PRIVATE · 40 X1 · $899.90 ($22.50/X1) · lifetime on all 40 · FREE SHIPPING */
  fortyX1BusinessPrivate89990: 'https://shop.gritvuegear.com/cart/48896733970525:1',
  /** FIFTY_X1_BUSINESS_PRIVATE_109990_VARIANT_ID · PRIVATE · 50 X1 · $1,099.90 ($22.00/X1) · lifetime on all 50 · FREE SHIPPING */
  fiftyX1BusinessPrivate109990: 'https://shop.gritvuegear.com/cart/48896734625885:1',
  /** SIXTY_X1_BUSINESS_PRIVATE_129990_VARIANT_ID · PRIVATE · 60 X1 · $1,299.90 ($21.67/X1) · lifetime on all 60 · FREE SHIPPING */
  sixtyX1BusinessPrivate129990: 'https://shop.gritvuegear.com/cart/48896741015645:1',
}

export type ProductLinkKey = keyof typeof PRODUCT_LINKS

/**
 * DEPRECATED · NOT USED IN LIVE ROUTING.
 * Kept only as a record of superseded products. These keys are not part of
 * ProductLinkKey, so no button can route a customer through them. Archive or
 * unpublish these products in Shopify.
 */
export const DEPRECATED_PRODUCT_LINKS = {
  /** Old $49.95 + free shipping retention (one-year coverage) · removed from the funnel */
  singleX1FreeShippingRetention4995: '',
  /** Old public 6-pack at $199.80 · its Shopify variant (45095747879005) is now reused live by sixX1Public17990 */
  sixX1Public19980: '',
  /** Old public 30-pack at $999.00 · its Shopify variant (45095754858589) is now reused live by thirtyX1Public69990 */
  thirtyX1Public99900: '',
  /** Old private 5 X1 at $149.90 */
  fiveX1Private14990: '',
  /** Old private 6 X1 at $179.90 */
  sixX1Private17990: '',
  /** Old private 8 X1 at $239.90 */
  eightX1Private23990: '',
  /** Old private 10 X1 at $299.90 */
  tenX1Private29990: '',
  /** Old private 12 X1 at $359.90 */
  twelveX1Private35990: '',
  /** Old business 40 X1 at $1,199 */
  fortyX1BusinessPrivate119900: '',
  /** Old business 50 X1 at $1,499 */
  fiftyX1BusinessPrivate149900: '',
  /** Old business 60 X1 at $1,799 */
  sixtyX1BusinessPrivate179900: '',
} as const

const SHOP_CART_URL = 'https://shop.gritvuegear.com/cart'

/** The Shopify variant ID inside a link (or the link itself if it is a bare ID). */
export function variantIdOf(link: string): string {
  const match = link.match(/(\d{6,})(?::\d+)?\/?$/)
  return match ? match[1] : link.trim()
}

/** Cart URL for a product at a given quantity, or '' when the link is not set yet. */
export function cartLink(key: ProductLinkKey, quantity = 1): string {
  const raw = PRODUCT_LINKS[key].trim()
  if (!raw) return ''
  const id = variantIdOf(raw)
  if (!/^\d+$/.test(id)) return raw
  const base = /^https?:\/\//.test(raw) ? raw.replace(/\/cart\/.*$/, '/cart') : SHOP_CART_URL
  return `${base}/${id}:${quantity}`
}
